import { useAppStore } from "@/controller";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeftIcon, CloseIcon, StarIcon } from "@/components/Icons";
import { logClientEvent } from "@/telemetry";
import AlertWidgetPaywall, {
  restoreNativePurchase,
} from "@/components/AlertWidgetPaywall";
import {
  getPackagePricePresentation,
  getYearlySavingsPct,
} from "@/models/paywallMath";
import type { PurchasePackage } from "@/types";
import { toast } from "react-hot-toast";
import { useAuth } from "@/models/useAuth";
import { getPlatform, getPlatformRuntime, isNativeApp } from "@/platform";
import { gotoTerms, gotoPrivacy } from "@/product-links";
import { actions } from "@/controller";
import PersonaPicker from "@/components/onboarding/PersonaPicker";
import IntentPicker from "@/components/onboarding/IntentPicker";
import AhaDemo from "@/components/onboarding/AhaDemo";
import OnboardingReview from "@/components/onboarding/OnboardingReview";
import PaywallBenefits from "@/components/onboarding/PaywallBenefits";
import { requestReview } from "@/models/useNative";
import {
  getIntentOrder,
  OnboardingIntent,
  Persona,
} from "@/models/onboardingPersona";
import {
  getNextOnboardingStepIndex,
  getOnboardingPurchaseEffect,
  getOnboardingSteps,
  getPreviousOnboardingStepIndex,
  isOnboardingContinueDisabled,
  type PortableOnboardingStep,
} from "@/onboarding-interactions";
import { getStoreProofTranslationValues } from "@/product-proof";

const playAnnualNudgeHaptic = () => {
  if (getPlatform() === "android") navigator.vibrate?.(10);
};

const AlertWidgetOnboarding = () => {
  const { t, i18n } = useTranslation();
  const storeProof = getStoreProofTranslationValues(
    i18n.resolvedLanguage || i18n.language,
  );
  const hasCompletedOnboarding = useAppStore(
    (state) => state.hasCompletedOnboarding,
  );
  const setHasCompletedOnboarding = useAppStore(
    (state) => state.setHasCompletedOnboarding,
  );
  const isSubscribed = useAppStore((state) => state.isSubscribed);

  // Keep in-progress choices local for instant rendering and persist every
  // selection immediately so an interrupted onboarding can resume honestly.
  const initialState = useAppStore.getState();
  const initialPersona = initialState.onboardingPersona;
  const initialIntent = initialState.onboardingIntent;

  const [persona, setPersonaLocal] = useState<Persona | null>(initialPersona);
  const [intent, setIntentLocal] = useState<OnboardingIntent | null>(
    initialIntent,
  );

  const setPersona = (next: Persona) => {
    setPersonaLocal(next);
    useAppStore.getState().setOnboardingPersona(next);
  };

  const setIntent = (next: OnboardingIntent) => {
    setIntentLocal(next);
    useAppStore.getState().setOnboardingIntent(next);
  };

  // Every step advances only on an explicit Continue tap — mixing
  // auto-advancing and tap-to-advance steps made the flow unpredictable.
  const selectPersona = (next: Persona) => {
    // Only log actual changes — re-taps and mind-changes shouldn't stack
    // duplicate selection events in the funnel.
    if (next !== persona) {
      logClientEvent("onboarding.personaSelected", {
        persona: next,
      });
    }
    setPersona(next);
  };

  const selectIntent = (next: OnboardingIntent) => {
    if (next !== intent) {
      logClientEvent("onboarding.intentSelected", {
        intent: next,
        persona,
      });
    }
    setIntent(next);
  };

  const [stepIndex, setStepIndex] = useState(0);
  const [didPurchase, setDidPurchase] = useState(false);
  const [reviewRequestInFlight, setReviewRequestInFlight] = useState(false);
  const [reviewWasRequested, setReviewWasRequested] = useState(false);
  const reviewRequestInFlightRef = useRef(false);
  const [currentPackage, setCurrentPackage] = useState<
    PurchasePackage | undefined
  >(undefined);
  const [showAnnualNudge, setShowAnnualNudge] = useState(false);
  const revenueCatPackages = useAppStore(
    (state) => state.nativePurchase?.packages,
  );

  const stepOrder = useMemo(() => getOnboardingSteps(getPlatform()), []);
  const currentStep: PortableOnboardingStep | undefined = stepOrder[stepIndex];

  const packages = useMemo(() => revenueCatPackages, [revenueCatPackages]);

  const yearlyPackage = packages?.find((pkg) =>
    pkg.identifier.includes("annual"),
  );
  const weeklyPackage = packages?.find((pkg) =>
    pkg.identifier.includes("weekly"),
  );
  // Computed once — the badge and any savings copy must always agree.
  const yearlySavingsPct = useMemo(
    () => getYearlySavingsPct(yearlyPackage, weeklyPackage),
    [yearlyPackage, weeklyPackage],
  );

  // ── Lifecycle ────────────────────────────────────────────────────────────

  // True whenever this component should be fully dormant. Effects run even
  // when render returns null, so EVERY effect below must check this — a bare
  // hasCompletedOnboarding guard once let web sessions emit native onboarding
  // events (the appStateChange listener maps to tab visibility on web).
  //
  // `isSubscribed && !didPurchase`: a subscription that arrives from OUTSIDE
  // this flow (RevenueCat restore, status() on a reinstall) dismisses the
  // overlay — an active subscriber must never be pitched the paywall. A
  // purchase made INSIDE the flow flips isSubscribed before its callback
  // runs, but the callback sets didPurchase in the same batch, so the flow
  // survives to fire the intent deep link.
  const onboardingInactive =
    hasCompletedOnboarding || !isNativeApp() || (isSubscribed && !didPurchase);

  // Start loading purchase options while the user watches the product demo.
  useEffect(() => {
    if (onboardingInactive) return;
    if (
      stepIndex >= Math.max(0, stepOrder.indexOf("aha-demo")) &&
      !useAppStore.getState().nativePurchase?.packages
    ) {
      useAuth.signInAsGuest({
        callback: async () => {
          await actions.subscription.configureRevenueCat();
        },
      });
    }
  }, [stepIndex, stepOrder, onboardingInactive]);

  useEffect(() => {
    if (currentStep === "paywall") {
      setCurrentPackage(yearlyPackage ?? packages?.[0]);
    }
    setShowAnnualNudge(false);
  }, [currentStep, yearlyPackage, packages]);

  useEffect(() => {
    if (onboardingInactive) return;
    if (packages && packages.length > 0 && !currentPackage) {
      setCurrentPackage(yearlyPackage ?? packages[0]);
    }
  }, [packages, yearlyPackage, currentPackage, onboardingInactive]);

  // ── Product milestones ───────────────────────────────────────────────────
  useEffect(() => {
    if (onboardingInactive || !currentStep) return;
    logClientEvent("onboarding.stepViewed", {
      step: stepIndex,
      stepKey: currentStep,
      persona,
      intent,
    });
    if (currentStep === "aha-demo") {
      logClientEvent("onboarding.ahaDemoViewed", {
        persona,
        intent,
      });
    }
    if (currentStep === "review") {
      logClientEvent("onboarding.reviewPromptViewed", {
        persona,
        intent,
      });
    }
    // Fire once per step transition ONLY — persona/intent are read
    // fresh when the effect runs, but keeping them out of the deps stops the
    // same step being re-logged on every selection tap (which inflated
    // step-view denominators).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex, currentStep, onboardingInactive]);

  // Hardware back button — go back one step, unless we're on the first screen
  // or the paywall.
  useEffect(() => {
    if (onboardingInactive) return;
    const runtime = getPlatformRuntime();
    const backListener = runtime.app.onBackButton(() => {
      // The first screen has nothing to go back to and the paywall intentionally
      // blocks back-navigation — preserve Android's expected "back leaves
      // the app" behavior there instead (home's own minimize fallback is
      // suppressed while onboarding is active).
      if (stepIndex === 0 || currentStep === "paywall") {
        void runtime.app.minimize();
        return;
      }
      setStepIndex(stepIndex - 1);
    });
    return () => {
      backListener.then((listener) => listener.remove());
    };
  }, [onboardingInactive, stepIndex, currentStep]);

  // Abandonment evidence captures the last step the user reached in
  // this session. Sent on every step transition and on backgrounding so the
  // pipeline can derive "last step reached before drop-off". Best-effort —
  // doesn't catch all force-quit cases, but gives us a useful drop-off signal.
  useEffect(() => {
    if (onboardingInactive || !currentStep) return;
    const onStateChange = (state: { isActive: boolean }) => {
      if (state.isActive) return; // only fire on background
      logClientEvent("onboarding.abandoned", {
        lastStep: currentStep,
        lastStepIndex: stepIndex,
        persona,
        intent,
        didPurchase,
      });
    };
    const sub = getPlatformRuntime().app.onStateChange(onStateChange);
    return () => {
      sub.then((l) => l.remove());
    };
  }, [
    onboardingInactive,
    currentStep,
    stepIndex,
    persona,
    intent,
    didPurchase,
  ]);

  // ── Bail-out: don't render onboarding when complete or unsupported ───────
  if (onboardingInactive) {
    return null;
  }

  // ── Navigation helpers ───────────────────────────────────────────────────
  const goNext = () => {
    setStepIndex((s) => getNextOnboardingStepIndex(s, stepOrder.length));
  };
  const goBack = () => {
    setStepIndex(getPreviousOnboardingStepIndex);
  };
  // Completing onboarding only closes it. The user starts their first action
  // explicitly from Home, where permission prompts have immediate context.
  const finishOnboarding = () => {
    setHasCompletedOnboarding(true);
  };

  // ── Continue button state per step ───────────────────────────────────────
  const continueDisabled = isOnboardingContinueDisabled(currentStep, {
    hasPersona: Boolean(persona),
    hasIntent: Boolean(intent),
  });

  // ── Step-specific click handlers ─────────────────────────────────────────
  const handlePrimary = () => {
    // The CTA stays tappable when a selection is still missing — a truly
    // `disabled` button reads as "the app is stuck" (reported on Android).
    // Tapping it explains what to do instead of silently ignoring the tap.
    if (continueDisabled) {
      toast(t("onboarding.pickToContinue"), { id: "onboarding-pick" });
      return;
    }
    goNext();
  };

  const handleReviewAccept = async () => {
    if (reviewWasRequested) {
      goNext();
      return;
    }
    if (reviewRequestInFlightRef.current) return;
    reviewRequestInFlightRef.current = true;
    setReviewWasRequested(true);
    setReviewRequestInFlight(true);
    logClientEvent("onboarding.reviewChoice", { accepted: true });
    try {
      await requestReview();
      logClientEvent("onboarding.reviewRequestFinished", {
        status: "resolved",
      });
    } catch {
      logClientEvent("onboarding.reviewRequestFinished", {
        status: "failed",
      });
    } finally {
      reviewRequestInFlightRef.current = false;
      setReviewRequestInFlight(false);
      goNext();
    }
  };

  // ── Paywall handlers ─────────────────────────────────────────────────────
  const handlePurchase = () => {
    const purchaseEffect = getOnboardingPurchaseEffect(Boolean(currentPackage));
    if (purchaseEffect === "retryPackages") {
      // Packages haven't resolved (slow network / failed configure). A
      // silent no-op reads as a broken button — acknowledge the tap and
      // retry the RevenueCat setup so a recovered connection can still sell.
      toast(t("home.loading"), { id: "onboarding-packages" });
      useAuth.signInAsGuest({
        callback: async () => {
          await actions.subscription.configureRevenueCat();
        },
      });
      return;
    }
    if (!currentPackage) {
      throw new Error("Purchase effect requires a selected package");
    }
    const selectedPackage = currentPackage;
    logClientEvent("paywall.purchaseStarted", {
      package: selectedPackage.identifier,
      persona,
      intent,
    });
    AlertWidgetPaywall.handlePurchase({
      package: selectedPackage,
      source: "onboarding",
      callback: () => {
        logClientEvent("paywall.purchaseCompleted", {
          package: selectedPackage.identifier,
          persona,
          intent,
        });
        toast.success(t("alertWidgetPaywall.subscriptionSuccess"));
        setDidPurchase(true);
        finishOnboarding();
      },
    });
  };

  const handleSkipPaywall = () => {
    logClientEvent("paywall.skipped", {
      persona,
      intent,
    });
    finishOnboarding();
  };

  // ── Render helpers ────────────────────────────────────────────────────────
  const personaSafe: Persona | null = persona;
  const personaSuffix = personaSafe || "other";

  // ── Per-step body content ────────────────────────────────────────────────
  const renderStep = () => {
    switch (currentStep) {
      case "persona":
        return (
          <div className="flex w-full max-w-[520px] flex-col items-center text-center animate-in fade-in slide-in-from-bottom-4 duration-500">
            <h2 className="text-2xl font-black text-[var(--text-primary)] mb-3 tracking-tight">
              {t("onboarding.persona.headline")}
            </h2>
            <p className="text-[14px] text-[var(--text-secondary)] leading-relaxed mb-6">
              {t("onboarding.persona.subhead")}
            </p>
            <PersonaPicker
              actionId="onboarding.persona.select"
              value={persona}
              onChange={selectPersona}
            />
          </div>
        );

      case "intent":
        return (
          <div className="flex w-full max-w-[520px] flex-col items-center text-center animate-in fade-in slide-in-from-bottom-4 duration-500">
            <img
              src="/logo.png"
              alt="1Transcribe"
              width={56}
              height={56}
              className="mb-4 h-14 w-14 rounded-2xl object-contain drop-shadow-lg"
            />
            <h2 className="text-2xl font-black text-[var(--text-primary)] mb-2 tracking-tight">
              {t("onboarding.intent.headline")}
            </h2>
            <p className="text-[13.5px] text-[var(--text-secondary)] leading-relaxed mb-4">
              {t("onboarding.intent.subhead")}
            </p>
            <IntentPicker
              actionId="onboarding.intent.select"
              value={intent}
              onChange={selectIntent}
              order={getIntentOrder(null)}
            />
            <div className="mt-4 w-full rounded-2xl bg-[var(--success)]/10 px-4 py-3">
              <p className="text-[12.5px] font-bold leading-relaxed text-[var(--success)]">
                {t("onboarding.intent.freePromise")}
              </p>
            </div>
            <div className="mt-3 flex flex-wrap justify-center gap-2">
              <span className="rounded-full bg-[var(--fill-1)] px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-[var(--text-tertiary)]">
                {t("onboarding.welcome.badges")}
              </span>
              <span className="rounded-full bg-[var(--fill-1)] px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-[var(--text-tertiary)]">
                {t("alertWidgetPaywall.rating", storeProof)}
              </span>
            </div>
          </div>
        );

      case "aha-demo":
        return (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 w-full">
            <AhaDemo intent={intent || "record"} persona={personaSafe} />
          </div>
        );

      case "review":
        return <OnboardingReview />;

      case "paywall":
        return (
          <PaywallBenefits intent={intent} persona={personaSafe} setupReady />
        );

      default:
        return null;
    }
  };

  // ── CTA label per step ────────────────────────────────────────────────────
  const primaryCtaLabel = (() => {
    switch (currentStep) {
      case "persona":
        return t("onboarding.persona.cta");
      case "intent":
        return t("onboarding.intent.cta");
      case "aha-demo":
        return t("onboarding.ahaDemo.cta");
      default:
        return t("onboarding.continue");
    }
  })();

  const isPaywallStep = currentStep === "paywall";
  const isReviewStep = currentStep === "review";

  // The paywall is a decision surface, not another setup step.
  const dotSteps: PortableOnboardingStep[] = stepOrder.filter(
    (s) => s !== "paywall",
  );
  const dotIndex = currentStep ? dotSteps.indexOf(currentStep) : -1;

  return (
    <div className="fixed inset-0 z-[2000] flex flex-col bg-[var(--bg-secondary)] overflow-hidden">
      <style>{`
        @keyframes gentle-bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        .animate-gentle-bounce {
          animation: gentle-bounce 2s infinite ease-in-out;
        }
        @media (prefers-reduced-motion: reduce) {
          .animate-gentle-bounce {
            animation: none;
          }
        }
      `}</style>

      {/* Background Ambience */}
      <div className="absolute top-[-20%] left-[-10%] w-[70%] h-[50%] bg-[var(--onboarding-ambience-blue)] rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[60%] h-[40%] bg-[var(--onboarding-ambience-purple)] rounded-full blur-[100px] pointer-events-none" />

      {/* Top-left back chevron (hidden on the first screen and paywall) */}
      {stepIndex > 0 && !isPaywallStep && (
        <button
          data-action-id="onboarding.back"
          onClick={goBack}
          aria-label={t("globalDictationOnboarding.back")}
          className="icon-btn absolute z-[100] cursor-pointer active:scale-95 transition-transform duration-200 backdrop-blur-md"
          style={{
            top: "max(48px, calc(env(safe-area-inset-top) + 8px))",
            left: 16,
          }}
        >
          <ChevronLeftIcon
            size={22}
            className="text-[var(--text-primary)] opacity-80"
          />
        </button>
      )}

      {/* The free path is always one action away. The visible free-plan button
          in the footer uses this same direct dismissal behavior. */}
      {isPaywallStep && (
        <button
          data-action-id="onboarding.paywall.dismiss"
          onClick={handleSkipPaywall}
          aria-label={t("onboarding.paywall.skip")}
          className="icon-btn absolute z-[100] cursor-pointer active:scale-95 transition-transform duration-200 backdrop-blur-md"
          style={{
            top: "max(48px, calc(env(safe-area-inset-top) + 8px))",
            right: 16,
          }}
        >
          <CloseIcon
            size={20}
            className="text-[var(--text-primary)] opacity-80"
          />
        </button>
      )}

      {/* Content Area — always scrollable so the footer CTA is never pushed
          off-screen on small phones. Short steps still look centered via the
          inner my-auto wrapper below. */}
      <div className="flex-1 flex flex-col items-stretch overflow-y-auto overflow-x-hidden no-scrollbar relative z-10 px-6 pt-16 pb-4">
        <div className="my-auto w-full flex flex-col items-center">
          {renderStep()}
        </div>
      </div>

      {/* Footer Navigation — sticky bottom, always visible. The paywall uses a
          compact decision footer so its benefit headline stays above the fold
          on short phones; every other step keeps the roomier onboarding pad. */}
      <div
        className={`shrink-0 w-full relative z-10 bg-gradient-to-t from-[var(--bg-secondary)] via-[var(--bg-secondary)] to-transparent ${
          isPaywallStep ? "px-5 pt-3" : "p-6"
        }`}
        style={{
          paddingBottom: isPaywallStep
            ? "max(20px, calc(env(safe-area-inset-bottom) + 12px))"
            : "max(56px, calc(env(safe-area-inset-bottom) + 24px))",
        }}
      >
        {isPaywallStep ? (
          <div className="mx-auto flex w-full max-w-[520px] flex-col animate-in slide-in-from-bottom-8 duration-500">
            <div className="mb-3 flex flex-col gap-2">
              {[yearlyPackage, weeklyPackage].map((pkg) => {
                if (!pkg) return null;
                const isAnnual = pkg.identifier.includes("annual");
                const isWeekly = pkg.identifier.includes("weekly");
                const pricePresentation = getPackagePricePresentation(pkg);
                const isSelected =
                  pkg.identifier === currentPackage?.identifier;
                return (
                  <button
                    data-action-id="onboarding.paywall.package.select"
                    onClick={() => {
                      const isNewWeeklySelection = isWeekly && !isSelected;
                      setCurrentPackage(pkg);
                      setShowAnnualNudge(isWeekly);
                      if (isNewWeeklySelection) playAnnualNudgeHaptic();
                    }}
                    key={pkg.identifier}
                    className={`group relative flex min-h-16 w-full cursor-pointer flex-row items-center justify-between rounded-2xl px-4 py-3 transition-[transform,background-color,box-shadow] duration-300
                      ${
                        isSelected
                          ? "selected-surface scale-[1.02]"
                          : "bg-[var(--fill-1)] hover:bg-[var(--fill-2)] active:scale-[0.98]"
                      }`}
                  >
                    {isAnnual && (
                      <div
                        className={`absolute -top-2.5 right-6 px-2.5 py-0.5 rounded-full z-[1] flex items-center gap-1 shadow-sm bg-[var(--accent)] ${
                          showAnnualNudge
                            ? "animate-in fade-in slide-in-from-bottom-2 duration-300 motion-reduce:animate-none"
                            : ""
                        }`}
                      >
                        {showAnnualNudge && (
                          <span
                            aria-hidden="true"
                            className="text-sm leading-none"
                          >
                            🐱
                          </span>
                        )}
                        <span className="text-[9px] font-extrabold uppercase tracking-wider text-[var(--bg-primary)]">
                          {/* Computed discount beats a vague superlative —
                              falls back to BEST OFFER when prices aren't
                              numeric (e.g. some web package shapes). */}
                          {showAnnualNudge
                            ? t(
                                "alertWidgetPaywall.annualPriceIncreasingSoon",
                                {
                                  defaultValue:
                                    "Current annual price increases soon",
                                },
                              )
                            : yearlySavingsPct
                              ? t("alertWidgetPaywall.savePct", {
                                  pct: yearlySavingsPct,
                                })
                              : t("alertWidgetPaywall.bestOffer")}
                        </span>
                      </div>
                    )}

                    <div className="flex flex-col items-start gap-0.5">
                      <span className="text-[15px] font-bold text-start text-[var(--text-primary)]">
                        {isAnnual
                          ? t("alertWidgetPaywall.yearlyAccess")
                          : t("alertWidgetPaywall.weeklyAccess")}
                      </span>
                      <span className="text-start text-[11.5px] font-medium text-[var(--text-secondary)]">
                        {isAnnual
                          ? t("alertWidgetPaywall.annualPricing", {
                              price: pkg.product.priceString,
                            })
                          : t("alertWidgetPaywall.premiumPlan")}
                      </span>
                    </div>

                    <div className="flex flex-col items-end">
                      <span className="text-[16px] font-bold text-[var(--text-primary)]">
                        {pricePresentation.priceString}
                      </span>
                      <span
                        className={`text-[11px] transition-colors duration-300 ${
                          isSelected
                            ? "text-[var(--text-primary)]"
                            : "text-[var(--text-secondary)]"
                        }`}
                      >
                        /
                        {t(
                          pricePresentation.billingPeriod === "annual"
                            ? "alertWidgetPaywall.perYear"
                            : pricePresentation.billingPeriod === "monthly"
                              ? "alertWidgetPaywall.perMonth"
                              : "alertWidgetPaywall.perWeek",
                        ).toLowerCase()}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="mb-1.5 px-1">
              <button
                data-action-id="onboarding.paywall.purchase"
                className="relative flex h-14 w-full items-center justify-center rounded-full bg-gradient-to-r from-[var(--premium-cta-start)] to-[var(--premium-cta-end)] text-[18px] font-bold tracking-wide text-[var(--premium-cta-text)] shadow-lg shadow-[var(--premium-cta-glow)] transition-transform active:scale-[0.98]"
                onClick={handlePurchase}
              >
                <span>{t("alertWidgetPaywall.buttonContinue")}</span>
              </button>
            </div>

            {/* Billing terms are only honest once we know the amount — an
                unresolved package would render "  billed now", which reads as
                a broken disclosure on exactly the slow networks where it
                happens most. */}
            {currentPackage?.product.priceString && (
              <p className="mb-1 text-center text-[10.5px] leading-4 text-[var(--text-secondary)]">
                {t("alertWidgetPaywall.billingDisclosure", {
                  price: currentPackage.product.priceString,
                })}
              </p>
            )}

            <button
              data-action-id="onboarding.paywall.dismiss"
              onClick={handleSkipPaywall}
              className="h-11 w-full text-center text-[14px] font-semibold text-[var(--text-tertiary)] transition-colors active:text-[var(--text-primary)]"
            >
              {t("onboarding.paywall.skip")}
            </button>

            <div className="flex flex-row justify-center gap-6">
              <button
                data-action-id="legal.terms.open"
                onClick={gotoTerms}
                className="text-[11px] font-medium text-[var(--text-tertiary)]"
              >
                {t("alertWidgetPaywall.terms")}
              </button>
              <button
                data-action-id="legal.privacy.open"
                onClick={gotoPrivacy}
                className="text-[11px] font-medium text-[var(--text-tertiary)]"
              >
                {t("alertWidgetPaywall.privacy")}
              </button>
              <button
                data-action-id="subscription.restore"
                onClick={restoreNativePurchase}
                className="text-[11px] font-medium text-[var(--text-tertiary)]"
              >
                {t("alertWidgetPaywall.restore")}
              </button>
            </div>
          </div>
        ) : isReviewStep ? (
          <div className="mx-auto flex w-full max-w-[520px] flex-col gap-5 animate-in slide-in-from-bottom-8 duration-500">
            {dotSteps.length > 0 && dotIndex >= 0 && (
              <div className="flex justify-center gap-2">
                {dotSteps.map((_, i) => (
                  <div
                    key={i}
                    className={`h-2 rounded-full transition-[width,background-color] duration-300 ${
                      i === dotIndex
                        ? "w-8 bg-[var(--accent)]"
                        : "w-2 bg-[var(--text-tertiary)]/30"
                    }`}
                  />
                ))}
              </div>
            )}
            <button
              data-action-id="onboarding.review.accept"
              onClick={() => void handleReviewAccept()}
              aria-busy={reviewRequestInFlight}
              className="w-full h-14 bg-gradient-to-r from-[var(--premium-cta-start)] to-[var(--premium-cta-end)] text-[var(--premium-cta-text)] rounded-full font-bold text-[17px] shadow-lg shadow-[var(--premium-cta-glow)] active:scale-[0.98] transition-transform flex items-center justify-center gap-2"
            >
              {!reviewWasRequested && <StarIcon size={18} />}
              {t(
                reviewWasRequested
                  ? "onboarding.continue"
                  : "onboarding.review.cta",
              )}
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            {dotSteps.length > 0 && dotIndex >= 0 && (
              <div className="flex justify-center gap-2">
                {dotSteps.map((_, i) => (
                  <div
                    key={i}
                    className={`h-2 rounded-full transition-[width,background-color] duration-300 ${
                      i === dotIndex
                        ? "w-8 bg-[var(--accent)]"
                        : "w-2 bg-[var(--text-tertiary)]/30"
                    }`}
                  />
                ))}
              </div>
            )}

            <button
              data-action-id="onboarding.continue"
              onClick={handlePrimary}
              aria-disabled={continueDisabled}
              className={`w-full h-14 rounded-full font-bold text-[17px] shadow-lg transition-[transform,background-color,color,box-shadow] flex items-center justify-center gap-2 ${
                continueDisabled
                  ? "bg-[var(--fill-3)] text-[var(--text-tertiary)]"
                  : "bg-[var(--text-primary)] text-[var(--bg-primary)] active:scale-[0.98]"
              }`}
            >
              {primaryCtaLabel}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertWidgetOnboarding;
